import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
} from "@chakra-ui/react";
import { getInboxes, getReplaceableAddress, isDTag, isReplaceable } from "applesauce-core/helpers";
import { getInvoice } from "applesauce-common/helpers";
import { getOutboxes, mergeRelaySets } from "applesauce-core/helpers";
import { getZapSplits } from "applesauce-common/helpers/zap";
import { firstValueFrom } from "applesauce-core/observable";
import dayjs from "dayjs";
import { EventTemplate, kinds, NostrEvent } from "nostr-tools";
import { useState } from "react";
import { useActionRunner } from "applesauce-react/hooks";
import { TokensOperation } from "applesauce-wallet/actions";
import { getNutzapInfoMints, getNutzapInfoP2PKPubkey } from "applesauce-wallet/helpers";
import type { Token } from "@cashu/cashu-ts";

import accounts from "../../services/accounts";
import { eventStore } from "../../services/event-store";
import { recordTiming } from "../../services/telemetry";
import lnurlMetadataService from "../../services/lnurl-metadata";
import { getEventRelayHints } from "../../services/relay-hints";
import relayScoreboardService from "../../services/relay-scoreboard";
import couch from "../../services/cashu-couch";
import pool from "../../services/pool";
import UserLink from "../user/user-link";
import InputStep from "./input-step";
import PayStep from "./pay-step";
import NutzapPayStep from "./nutzap-pay-step";

export type PayRequest = { invoice?: string; pubkey: string; error?: any };

// TODO: this is way to complicated, it needs to be broken into multiple parts / hooks

/**
 *
 * @param pubkey pubkey to be zapped
 * @param event event to be zapped
 * @param amount amount in msats
 * @param comment zap comment
 * @param additionalRelays extra relays to set the zap to
 * @returns
 */
export async function getPayRequestForPubkey(
  pubkey: string,
  event: NostrEvent | undefined,
  amount: number,
  comment?: string,
  additionalRelays?: Iterable<string>,
): Promise<PayRequest> {
  const metadata = await firstValueFrom(eventStore.profile(pubkey));
  if (!metadata) throw new Error("Cant find user metadata");
  const address = metadata?.lud16 || metadata?.lud06;
  if (!address) throw new Error("User missing lightning address");
  const lnurlMetadata = await lnurlMetadataService.requestMetadata(address);

  if (!lnurlMetadata) throw new Error("LNURL endpoint unreachable");

  if (amount > lnurlMetadata.maxSendable) throw new Error("Amount to large");
  if (amount < lnurlMetadata.minSendable) throw new Error("Amount to small");

  const canZap = !!lnurlMetadata.allowsNostr && !!lnurlMetadata.nostrPubkey;
  if (!canZap) {
    // build LNURL callback url
    const callback = new URL(lnurlMetadata.callback);
    callback.searchParams.append("amount", String(amount));
    if (comment) callback.searchParams.append("comment", comment);

    const invoice = await getInvoice(callback);

    return { invoice, pubkey };
  }

  const account = accounts.active;

  const mailboxes = eventStore.getReplaceable(kinds.RelayList, pubkey);
  const userInbox = mailboxes ? getInboxes(mailboxes).slice(0, 4) : [];
  const eventRelays = event ? getEventRelayHints(event, 2) : [];
  const accountMailboxes = account ? eventStore.getReplaceable(kinds.RelayList, account?.pubkey) : undefined;
  const outbox = relayScoreboardService
    .getRankedRelays(accountMailboxes ? getOutboxes(accountMailboxes) : [])
    .slice(0, 2);
  const additional = additionalRelays ? relayScoreboardService.getRankedRelays(additionalRelays) : [];

  // create zap request
  const zapRequest: EventTemplate = {
    kind: kinds.ZapRequest,
    created_at: dayjs().unix(),
    content: comment ?? "",
    tags: [
      ["p", pubkey],
      ["relays", ...mergeRelaySets(userInbox, eventRelays, outbox, additional)],
      ["amount", String(amount)],
    ],
  };

  // attach "e" or "a" tag
  if (event) {
    if (isReplaceable(event.kind) && event.tags.some(isDTag)) {
      const address = getReplaceableAddress(event);
      if (address) zapRequest.tags.push(["a", address]); // v5: can return null
    } else zapRequest.tags.push(["e", event.id]);
  }

  // TODO: move this out to a separate step so the user can choose when to sign
  if (!account) throw new Error("No Account");
  const signed = await account.signEvent(zapRequest);

  // build LNURL callback url
  const callback = new URL(lnurlMetadata.callback);
  callback.searchParams.append("amount", String(amount));
  callback.searchParams.append("nostr", JSON.stringify(signed));

  const invoice = await getInvoice(callback);

  return { invoice, pubkey };
}

async function getPayRequestsForEvent(
  event: NostrEvent,
  amount: number,
  comment?: string,
  fallbackPubkey?: string,
  additionalRelays?: Iterable<string>,
) {
  const splits = getZapSplits(event) ?? (fallbackPubkey ? [{ pubkey: fallbackPubkey, percent: 1, weight: 1 }] : []);

  const draftZapRequests: PayRequest[] = [];
  for (const { pubkey, percent } of splits) {
    try {
      // NOTE: round to the nearest sat since there isn't support for msats yet
      const splitAmount = Math.round((amount / 1000) * percent) * 1000;
      draftZapRequests.push(await getPayRequestForPubkey(pubkey, event, splitAmount, comment, additionalRelays));
    } catch (e) {
      draftZapRequests.push({ error: e, pubkey });
    }
  }

  return draftZapRequests;
}

export type ZapModalProps = Omit<ModalProps, "children"> & {
  pubkey: string;
  event?: NostrEvent;
  relays?: string[];
  initialComment?: string;
  initialAmount?: number;
  allowComment?: boolean;
  showEmbed?: boolean;
  additionalRelays?: Iterable<string>;
  onZapped: () => void;
  nutzapInfo?: NostrEvent;
};

export default function ZapModal({
  event,
  pubkey,
  relays,
  onClose,
  initialComment,
  initialAmount,
  allowComment = true,
  showEmbed = true,
  additionalRelays = [],
  onZapped,
  nutzapInfo,
  ...props
}: ZapModalProps) {
  const actions = useActionRunner();
  const [callbacks, setCallbacks] = useState<PayRequest[]>();
  const [nutzapState, setNutzapState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [nutzapError, setNutzapError] = useState<string>();

  const useNutzap = !callbacks;

  const handleNutzapSubmit = async (values: { amount: number; comment: string }) => {
    if (!event) return;
    const tipStart = performance.now();

    // Use recipient's nutzap info if available, otherwise fall back to their nostr pubkey
    const p2pkPubkey = nutzapInfo ? getNutzapInfoP2PKPubkey(nutzapInfo) : pubkey;
    if (!p2pkPubkey) throw new Error("Could not determine recipient P2PK pubkey");

    const recipientMints = nutzapInfo ? getNutzapInfoMints(nutzapInfo).map((m) => m.mint) : [];
    const targetMint = recipientMints[0]; // undefined if no nutzap info → uses sender's mint

    setNutzapState("sending");
    setNutzapError(undefined);

    try {
      let sendToken: Token | null = null;

      await actions.run(
        TokensOperation,
        values.amount,
        async ({ selectedProofs, mint, cashuWallet }) => {
          const { send, keep } = await cashuWallet.ops
            .send(values.amount, selectedProofs)
            .asP2PK({ pubkey: p2pkPubkey })
            .run();
          sendToken = { mint, proofs: send, unit: "sat" };
          return { change: keep };
        },
        targetMint ? { mint: targetMint, couch } : { couch },
      );

      if (sendToken) {
        // Manually create and publish kind 9321 nutzap event
        // (bypasses NutzapEvent action which has a silent catch {} that swallows publish errors)
        const account = accounts.active;
        if (!account) throw new Error("No active account");
        const token = sendToken as Token;

        const nutzapTemplate: EventTemplate = {
          kind: 9321,
          created_at: dayjs().unix(),
          content: values.comment || "",
          tags: [
            ...token.proofs.map((proof) => ["proof", JSON.stringify(proof)]),
            ["u", token.mint],
            ["e", event.id],
            ["p", event.pubkey],
          ],
        };

        const signed = await account.signEvent(nutzapTemplate);
        const walletRelays = ["wss://nostr.litstrudel.xyz"];
        const publishResults: string[] = [];
        await new Promise<void>((resolve) => {
          pool.event(walletRelays, signed).subscribe({
            next: (r) => {
              if (r.ok) publishResults.push(r.from);
            },
            complete: () => resolve(),
          });
        });
        if (publishResults.length === 0) {
          throw new Error("Failed to publish nutzap event to any relay");
        }
        console.log("Nutzap published to:", publishResults);
      }

      setNutzapState("sent");
      recordTiming({
        type: "tip",
        durationMs: performance.now() - tipStart,
        timestamp: Date.now(),
        amount: values.amount,
      });
      onZapped();
    } catch (e) {
      setNutzapState("error");
      setNutzapError(e instanceof Error ? e.message : String(e));
    }
  };

  const renderContent = () => {
    if (nutzapState === "sending" || nutzapState === "sent" || nutzapState === "error") {
      return (
        <NutzapPayStep
          state={nutzapState}
          error={nutzapError}
          pubkey={pubkey}
          onComplete={onClose}
          onRetry={() => setNutzapState("idle")}
        />
      );
    }

    if (callbacks && callbacks.length > 0) {
      return <PayStep callbacks={callbacks} onComplete={onZapped} />;
    }

    return (
      <InputStep
        pubkey={pubkey}
        event={event}
        initialComment={initialComment}
        initialAmount={initialAmount}
        showEmbed={showEmbed}
        allowComment={allowComment}
        isNutzap={useNutzap}
        onSubmit={async (values) => {
          if (useNutzap) {
            await handleNutzapSubmit(values);
          } else {
            const amountInMSats = values.amount * 1000;
            if (event) {
              setCallbacks(
                await getPayRequestsForEvent(event, amountInMSats, values.comment, pubkey, additionalRelays),
              );
            } else {
              const callback = await getPayRequestForPubkey(
                pubkey,
                event,
                amountInMSats,
                values.comment,
                additionalRelays,
              );
              setCallbacks([callback]);
            }
          }
        }}
      />
    );
  };

  return (
    <Modal onClose={onClose} size="xl" {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader px="4" pb="0" pt="4">
          {event ? (
            "Lit Event"
          ) : (
            <>
              Lit <UserLink pubkey={pubkey} fontWeight="bold" />
            </>
          )}
        </ModalHeader>
        <ModalBody padding="4">{renderContent()}</ModalBody>
      </ModalContent>
    </Modal>
  );
}
